// src/Root.tsx
import React, { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import CustomErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import ImpersonationBanner from "./components/ImpersonationBanner/ImpersonationBanner";
import { FrontendMigrationChoiceModal } from "./components/FrontendMigration";
import { shouldShowOwnerMigrationModal } from "@/utils/frontendMigration";
import { useAuthStore } from "@utils/stores/authStore.ts";
import { exchangeAdminLoginToken } from "@pages/LoginManagement/api.ts";
import {
  refreshIntercomUserContext,
  initializeIntercom,
  shutdownIntercom,
} from "@utils/intercom";
import { emitDebugEvent } from "@utils/debug/eventIntegration";
import { useDebugRouteTracking } from "./hooks/useDebugRouteTracking";

const Root: React.FC = () => {
  const { checkAuthForPath, user } = useAuthStore();
  const location = useLocation();
  const isAdminLoginFlowRef = useRef(false);

  const isAdminLoginPath = /^\/admin-login\/([^\/]+)\/?$/.test(
    location.pathname
  );
  const shouldSkipDebugTracking =
    isAdminLoginPath || isAdminLoginFlowRef.current;

  // Handle admin login session token exchange
  // This exchanges a one-time session token for JWT tokens with httponly cookies
  useEffect(() => {
    if (isAdminLoginPath) {
      const adminLoginMatch = location.pathname.match(
        /^\/admin-login\/([^\/]+)\/?$/
      );
      const sessionToken = adminLoginMatch?.[1];

      if (sessionToken) {
        // Mark that we're starting an admin-login flow
        isAdminLoginFlowRef.current = true;

        // exchangeAdminLoginToken will redirect directly to the backend
        // which will set cookies and redirect back to the app
        exchangeAdminLoginToken(sessionToken, location.search);
      }
    }
  }, [isAdminLoginPath, location.pathname, location.search]);

  useEffect(() => {
    if (!isAdminLoginPath && !isAdminLoginFlowRef.current) {
      checkAuthForPath(location.pathname);
    }
  }, [isAdminLoginPath, location.pathname, checkAuthForPath]);

  // Track route changes and page loads for debug events
  useDebugRouteTracking({
    locationPathname: location.pathname,
    locationSearch: location.search,
    userId: user?.id,
    skip: shouldSkipDebugTracking,
  });

  // Initialize Intercom when user logs in, re-initialize if user changes
  useEffect(() => {
    if (isAdminLoginPath || isAdminLoginFlowRef.current) {
      return;
    }

    if (user) {
      // User is logged in - initialize Intercom
      console.log("[Root] User detected, initializing Intercom...");
      initializeIntercom().catch((error) => {
        console.error("[Root] Failed to initialize Intercom:", error);
      });
    } else {
      // User is logged out - shutdown Intercom
      console.log("[Root] No user detected, shutting down Intercom...");
      shutdownIntercom();
    }
  }, [user, location.pathname]);

  // Listen for extension heartbeat events to refresh Intercom
  // The extension should send a message with type "EXTENSION_HEARTBEAT" after successful heartbeat
  useEffect(() => {
    // Sample heartbeat events (emit every 10th heartbeat to reduce noise)
    let heartbeatCount = 0;
    const HEARTBEAT_SAMPLE_RATE = 10;

    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from the same origin
      if (event.origin !== window.location.origin) return;

      const message = event.data;

      // Handle extension heartbeat notification
      // Extension should send: { type: "EXTENSION_HEARTBEAT", version: "x.y.z" }
      if (message?.type === "EXTENSION_HEARTBEAT") {
        console.log(
          "[Root] Extension heartbeat detected, refreshing Intercom extension status"
        );

        heartbeatCount += 1;

        // Only emit debug event every Nth heartbeat to reduce noise
        // Heartbeats are frequent and not critical for debugging
        if (heartbeatCount % HEARTBEAT_SAMPLE_RATE === 0) {
          emitDebugEvent("system", "EXT_HEARTBEAT", "web", {
            version: message.version || "unknown",
            heartbeatCount: heartbeatCount.toString(),
          });
        }

        refreshIntercomUserContext("extension").catch((error) => {
          console.error(
            "[Root] Failed to refresh Intercom after extension heartbeat:",
            error
          );
        });
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <CustomErrorBoundary>
      <ImpersonationBanner />
      <FrontendMigrationChoiceModal
        opened={Boolean(
          user && shouldShowOwnerMigrationModal(user, location.pathname)
        )}
      />
      <Outlet />
    </CustomErrorBoundary>
  );
};

export default Root;
